import { useCallback, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Icon, type IconName } from '../design/icons';

// Tree: nested nodes with expand/collapse, roving focus, ←/→/↑/↓/Home/End, Enter selects.
// Generic — categories, client hierarchies, project phases → tasks.

export interface TreeNode {
  id: string;
  label: ReactNode;
  icon?: IconName;
  children?: TreeNode[];
  badge?: ReactNode;
  disabled?: boolean;
}

export interface TreeProps {
  nodes: TreeNode[];
  selected?: string | null;
  onSelect?: (id: string, node: TreeNode) => void;
  expanded?: Set<string>;
  onExpandedChange?: (next: Set<string>) => void;
  defaultExpandAll?: boolean;
  'aria-label'?: string;
}

interface Flat {
  node: TreeNode;
  depth: number;
  parent: string | null;
  hasChildren: boolean;
}

export function Tree({ nodes, selected, onSelect, expanded: expandedProp, onExpandedChange, defaultExpandAll, 'aria-label': ariaLabel }: TreeProps) {
  const [expandedLocal, setExpandedLocal] = useState<Set<string>>(() => {
    if (!defaultExpandAll) return new Set();
    const all = new Set<string>();
    const walk = (ns: TreeNode[]) => ns.forEach((n) => (n.children?.length ? (all.add(n.id), walk(n.children)) : null));
    walk(nodes);
    return all;
  });
  const expanded = expandedProp ?? expandedLocal;
  const setExpanded = useCallback((s: Set<string>) => (onExpandedChange ? onExpandedChange(s) : setExpandedLocal(s)), [onExpandedChange]);
  const [focusId, setFocusId] = useState<string | null>(null);

  const flat = useMemo(() => {
    const out: Flat[] = [];
    const walk = (ns: TreeNode[], depth: number, parent: string | null) => {
      for (const n of ns) {
        const hasChildren = Boolean(n.children?.length);
        out.push({ node: n, depth, parent, hasChildren });
        if (hasChildren && expanded.has(n.id)) walk(n.children!, depth + 1, n.id);
      }
    };
    walk(nodes, 0, null);
    return out;
  }, [nodes, expanded]);

  const toggle = (id: string) => {
    const n = new Set(expanded);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    setExpanded(n);
  };

  const onKey = (e: KeyboardEvent, i: number) => {
    const f = flat[i]!;
    const go = (j: number) => {
      const t = flat[Math.max(0, Math.min(j, flat.length - 1))];
      if (t) {
        setFocusId(t.node.id);
        (e.currentTarget.parentElement?.querySelector(`[data-id="${CSS.escape(t.node.id)}"]`) as HTMLElement | null)?.focus();
      }
    };
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        go(i + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        go(i - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (f.hasChildren && !expanded.has(f.node.id)) toggle(f.node.id);
        else if (f.hasChildren) go(i + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (f.hasChildren && expanded.has(f.node.id)) toggle(f.node.id);
        else if (f.parent) go(flat.findIndex((x) => x.node.id === f.parent));
        break;
      case 'Home':
        e.preventDefault();
        go(0);
        break;
      case 'End':
        e.preventDefault();
        go(flat.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!f.node.disabled) onSelect?.(f.node.id, f.node);
        break;
    }
  };

  const focusable = focusId ?? selected ?? flat[0]?.node.id ?? null;

  return (
    <ul className="hf-tree" role="tree" aria-label={ariaLabel}>
      {flat.map((f, i) => {
        const isSel = selected === f.node.id;
        const open = expanded.has(f.node.id);
        return (
          <li
            key={f.node.id}
            role="treeitem"
            data-id={f.node.id}
            aria-level={f.depth + 1}
            aria-expanded={f.hasChildren ? open : undefined}
            aria-selected={isSel}
            aria-disabled={f.node.disabled || undefined}
            tabIndex={focusable === f.node.id ? 0 : -1}
            className={['hf-tree-item', isSel && 'selected', f.node.disabled && 'disabled'].filter(Boolean).join(' ')}
            style={{ paddingLeft: 8 + f.depth * 18 }}
            onKeyDown={(e) => onKey(e, i)}
            onFocus={() => setFocusId(f.node.id)}
            onClick={() => !f.node.disabled && onSelect?.(f.node.id, f.node)}
          >
            <span
              className={['tw', !f.hasChildren && 'leaf'].filter(Boolean).join(' ')}
              onClick={(e) => {
                if (!f.hasChildren) return;
                e.stopPropagation();
                toggle(f.node.id);
              }}
              aria-hidden
            >
              {f.hasChildren && <Icon name={open ? 'chevron-down' : 'chevron-right'} size={13} />}
            </span>
            {f.node.icon && <Icon name={f.node.icon} size={14} className="hf-muted" />}
            <span className="lbl hf-cell-trunc">{f.node.label}</span>
            {f.node.badge != null && <span className="bdg">{f.node.badge}</span>}
          </li>
        );
      })}
    </ul>
  );
}
